import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Clock, Eye, EyeSlash, CheckCircle } from '@phosphor-icons/react';

const formatApiError = (detail) => {
  if (detail == null) return "Une erreur s'est produite. Veuillez réessayer.";
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  }
  if (detail?.msg) return detail.msg;
  return String(detail);
};

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name })
      });
      const data = await response.json();
      if (!response.ok) {
        setError(formatApiError(data.detail));
      } else if (data.pending) {
        setPendingApproval(true);
      } else {
        navigate('/login');
      }
    } catch (err) {
      setError(formatApiError(err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div 
        className="login-image" 
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1559209537-dafe2fe2886b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBzd2lzcyUyMG9mZmljZSUyMGFyY2hpdGVjdHVyZSUyMGludGVyaW9yfGVufDB8fHx8MTc3NTY1NjgxNnww&ixlib=rb-4.1.0&q=85)' }}
      />
      <div className="login-form-container">
        <div className="login-form">
          <div className="login-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
              <img src="/batiboys-logo.svg" alt="BATI-BOYS" style={{ height: '40px', objectFit: 'contain' }} />
              <span className="sidebar-logo">BATI-BOYS</span>
            </div>
            <h1 className="login-title">Inscription</h1>
            <p className="login-subtitle">Créez votre compte employé</p>
          </div>

          {error && (
            <div data-testid="register-error" style={{ 
              padding: '12px 16px', 
              backgroundColor: '#FFEBEE', 
              color: '#C62828',
              borderRadius: '2px',
              marginBottom: '20px',
              fontSize: '0.875rem'
            }}>
              {error}
            </div>
          )}

          {pendingApproval ? (
            <div data-testid="pending-approval-message" style={{
              padding: '24px',
              backgroundColor: '#E8F5E9',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <CheckCircle size={48} weight="fill" style={{ color: '#2E7D32', marginBottom: '12px' }} />
              <h2 style={{ color: '#2E7D32', marginBottom: '8px', fontSize: '1.2rem' }}>Inscription enregistree</h2>
              <p style={{ color: '#5E5F66', marginBottom: '16px' }}>Votre compte est en attente de validation par un administrateur. Vous recevrez l'acces une fois approuve.</p>
              <Link to="/login" className="btn btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
                Retour a la connexion
              </Link>
            </div>
          ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Nom complet</label>
              <input
                data-testid="register-name-input"
                type="text"
                className="form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jean Dupont"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                data-testid="register-email-input"
                type="email"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Mot de passe</label>
              <div style={{ position: 'relative' }}>
                <input
                  data-testid="register-password-input"
                  type={showPassword ? 'text' : 'password'}
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  style={{ paddingRight: '48px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#5E5F66'
                  }}
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Confirmer le mot de passe</label>
              <input
                data-testid="register-confirm-password-input"
                type="password"
                className="form-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>

            <button
              data-testid="register-submit-btn"
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%', marginTop: '8px' }}
              disabled={loading}
            >
              {loading ? 'Inscription...' : "S'inscrire"}
            </button>
          </form>
          )}

          <p style={{ marginTop: '24px', textAlign: 'center', color: '#5E5F66' }}>
            Déjà un compte ?{' '}
            <Link to="/login" style={{ color: '#002FA7', textDecoration: 'none', fontWeight: '600' }}>
              Se connecter
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
